import { getStoredLoginIdentifier, getSessionToken } from '../auth/sessionToken';
import { isOwnerLogoImageUrl } from './profileWorkerIdentity';

const STORAGE_KEY_PREFIX = 'ofrontend.profileAvatarLocal';
const LEGACY_STORAGE_KEY = 'ofrontend.profileAvatarLocal';

export const PROFILE_AVATAR_UPDATED_EVENT = 'ofrontend:profile-avatar-updated';

function notifyProfileAvatarUpdated(): void {
  window.dispatchEvent(new CustomEvent(PROFILE_AVATAR_UPDATED_EVENT));
}

function profileAvatarStorageKey(): string {
  const loginId = getStoredLoginIdentifier()?.trim().toLowerCase();
  if (loginId) {
    return `${STORAGE_KEY_PREFIX}:${loginId}`;
  }
  const token = getSessionToken()?.trim();
  if (token) {
    return `${STORAGE_KEY_PREFIX}:token:${token.slice(-24)}`;
  }
  return `${STORAGE_KEY_PREFIX}:anonymous`;
}

function migrateLegacyProfileAvatarStorage(): void {
  try {
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacy?.trim()) {
      return;
    }
    const key = profileAvatarStorageKey();
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, legacy.trim());
    }
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function loadLocalProfileAvatarSrc(): string | null {
  try {
    migrateLegacyProfileAvatarStorage();
    const raw = localStorage.getItem(profileAvatarStorageKey());
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

export function saveLocalProfileAvatarSrc(dataUrl: string): void {
  try {
    localStorage.setItem(profileAvatarStorageKey(), dataUrl.trim());
    notifyProfileAvatarUpdated();
  } catch {
    /* quota or private mode */
  }
}

/** Cache a server-stored avatar on this device (URL or compact data URL). */
export function cacheProfileAvatarFromServer(avatarSrc: string): void {
  const trimmed = avatarSrc.trim();
  if (!trimmed || isOwnerLogoImageUrl(trimmed)) {
    return;
  }
  if (trimmed.startsWith('data:') && trimmed.length > 250_000) {
    return;
  }
  saveLocalProfileAvatarSrc(trimmed);
}

/**
 * Replace a stale per-device URL cache when GET /profile returns a different photo.
 * Keeps pending data-URL crops (upload in progress).
 */
export function syncLocalProfileAvatarWithServer(serverAvatarSrc: string | null | undefined): void {
  const server = serverAvatarSrc?.trim();
  if (!server || isOwnerLogoImageUrl(server)) {
    return;
  }
  const local = loadLocalProfileAvatarSrc();
  if (local?.startsWith('data:image/')) {
    return;
  }
  if (!local || local !== server) {
    saveLocalProfileAvatarSrc(server);
  }
}

export function clearLocalProfileAvatarSrc(): void {
  try {
    localStorage.removeItem(profileAvatarStorageKey());
    notifyProfileAvatarUpdated();
  } catch {
    /* ignore */
  }
}
